import {UnifiedIntegration} from "@/types/integrations";
import {normalize, normalizeAlphaNum} from "@/utils/integrations/marketplace-config";

export interface IntegrationWithSchema {
   name: string;
   description: string;
   serverCredentialSchema?: any;
   serverCredentialsSchema?: any;
   settingsSchema?: any;
}

/**
 * Key used for FALLBACK_SCHEMAS and AGENCY_CONNECT_IDS lookups.
 * Lowercases and strips hyphens so "Agency-Zoom", "agency-zoom" and "agencyzoom" all map to
 * "agencyzoom", matching the lowercase keys of those tables.
 * Use canonical names in Galactus: AgencyZoom, Momentum, AMS360, HawkSoft (agency modal for AgencyZoom).
 */
export function getFallbackSchemaKey(nameSlug: string): string {
   return normalize(nameSlug).replaceAll("-", "");
}

const UUID_LIKE = /^[0-9a-f-]{32,36}$/i;
function isLikelyUuid(id: string): boolean {
   return id.length > 20 && UUID_LIKE.test(id);
}

/**
 * Match grid integration (from Galactus listing) to Raccoon integration with schema.
 * When integration.id is a Galactus UUID it is not used for matching; matching is by name only.
 */
export function findIntegrationWithSchema(
   availableIntegrations: IntegrationWithSchema[],
   integration: Pick<UnifiedIntegration, "id" | "name">
): IntegrationWithSchema | undefined {
   const integrationIdLower = normalize(integration.id);
   const integrationNameLower = normalize(integration.name);
   const integrationNameNormalized = normalizeAlphaNum(integration.name);
   const matchById = !isLikelyUuid(integration.id);

   return availableIntegrations.find((available) => {
      const availableNameLower = normalize(available.name);
      const availableNameNormalized = normalizeAlphaNum(available.name);

      return (
         (matchById &&
            (availableNameLower === integrationIdLower ||
               availableNameNormalized === integrationIdLower)) ||
         availableNameLower === integrationNameLower ||
         availableNameNormalized === integrationNameNormalized ||
         (integration.name.toLowerCase().includes("momentum") &&
            (availableNameNormalized === "momentum" ||
               availableNameLower.includes("momentum") ||
               availableNameLower.includes("nowcerts"))) ||
         (integration.name.toLowerCase().includes("hawksoft") &&
            (availableNameNormalized === "hawksoft" || availableNameLower.includes("hawksoft"))) ||
         (integration.name.toLowerCase().includes("agencyzoom") &&
            (availableNameNormalized === "agencyzoom" ||
               availableNameLower.includes("agencyzoom") ||
               availableNameLower.includes("agency zoom"))) ||
         (integration.name.toLowerCase().includes("ams360") &&
            (availableNameNormalized === "ams360" ||
               availableNameLower.includes("ams360") ||
               availableNameLower.includes("ams 360") ||
               availableNameLower.includes("vertafore")))
      );
   });
}

export function extractCredentialsSchema(
   integrationWithSchema: IntegrationWithSchema | undefined
): Record<string, unknown> | null {
   const schemaField =
      integrationWithSchema?.serverCredentialSchema ??
      integrationWithSchema?.serverCredentialsSchema;

   if (!schemaField) return null;

   try {
      const parsed = typeof schemaField === "string" ? JSON.parse(schemaField) : schemaField;
      const hasUsableProperties =
         parsed?.properties &&
         typeof parsed.properties === "object" &&
         Object.entries(parsed.properties).some(([_, value]: [string, any]) => !value.const);
      return hasUsableProperties ? parsed : null;
   } catch {
      return null;
   }
}

export function extractSettingsSchema(
   integrationWithSchema: IntegrationWithSchema | undefined
): Record<string, unknown> | undefined {
   const rawSchema = integrationWithSchema?.settingsSchema;
   if (!rawSchema) return undefined;
   try {
      return typeof rawSchema === "string" ? JSON.parse(rawSchema) : rawSchema;
   } catch {
      return undefined;
   }
}

export const FALLBACK_SCHEMAS: Record<string, any> = {
   ams360: {
      type: "object",
      properties: {
         type: {type: "string", const: "no_auth"},
      },
      required: ["type"],
   },
   agencyzoom: {
      type: "object",
      properties: {
         username: {type: "string", minLength: 1, title: "Username"},
         password: {type: "string", minLength: 1, title: "Password"},
      },
      required: ["username", "password"],
   },
   momentum: {
      type: "object",
      properties: {
         type: {type: "string", const: "client_credentials"},
         // Property names stay as Raccoon expects them; the titles are what the
         // customer actually has, their Momentum login. `titleKey` is a
         // UI-only label and never reaches the Raccoon payload.
         clientId: {
            type: "string",
            minLength: 1,
            title: "Username",
            titleKey: "partners:marketplaceUsername",
         },
         clientSecret: {
            type: "string",
            minLength: 1,
            title: "Password",
            titleKey: "partners:marketplacePassword",
         },
      },
      required: ["type", "clientId", "clientSecret"],
   },
   hawksoft: {
      type: "object",
      properties: {
         type: {type: "string", const: "no_auth"},
      },
      required: ["type"],
   },
};

// These are hardcoded fallbacks — handleConnect uses them instead of
// extractSettingsSchema(), so they must be updated manually when the backend
// changes. Worth deriving from extractSettingsSchema() in a future update.
export const FALLBACK_SETTINGS_SCHEMAS: Record<string, any> = {
   ams360: {
      type: "object",
      properties: {
         username: {type: "string", minLength: 1, title: "Username"},
         agencyNumber: {
            type: "string",
            minLength: 1,
            title: "Agency number of the registered agency on Ams360 portal.",
         },
         appAccessToAgencyKey: {
            type: "string",
            minLength: 1,
            title: "Application access key for the agency registered on the Ams360 portal.",
         },
      },
      required: ["username", "agencyNumber", "appAccessToAgencyKey"],
   },
   hawksoft: {
      type: "object",
      properties: {
         agencyId: {
            type: "integer",
            minimum: 1,
            title: "Agency Identifier",
         },
         officeId: {
            type: "integer",
            title: "Office ID",
         },
      },
      required: ["agencyId"],
   },
};
